import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import { CHART_COLOURS } from './ChartColours'
import { cn } from '@/lib/utils'

function ChartTooltip({ active, payload, clickable = false, valueNoun = 'devices' }) {
  if (!active || !payload?.length) return null
  const item = payload[0]?.payload
  if (!item) return null
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-sm shadow-md">
      <p className="font-medium text-foreground">{item.label}</p>
      <p className="text-muted-foreground">
        {item.value?.toLocaleString('en-GB')} {valueNoun}
      </p>
      {clickable ? (
        <p className="mt-1 text-xs text-muted-foreground">Click to view {valueNoun}</p>
      ) : null}
    </div>
  )
}

function PieLegend({ data, formatLabel = (l) => l, activeLabel, onItemClick, colourFor }) {
  const total = data.reduce((sum, d) => sum + (d.value || 0), 0)
  return (
    <ul className="min-h-0 max-h-[220px] flex-1 space-y-2 overflow-y-auto pr-1 text-sm">
      {data.map((entry, index) => {
        const pct = total ? Math.round((entry.value / total) * 100) : 0
        const isActive = activeLabel === entry.label
        const isDimmed = activeLabel && !isActive
        const colour =
          colourFor?.(entry, index) ?? CHART_COLOURS[index % CHART_COLOURS.length]
        const content = (
          <>
            <span
              className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: colour }}
              aria-hidden
            />
            <span className="min-w-0 flex-1 leading-snug">
              <span className="block font-medium text-foreground" title={entry.label}>
                {formatLabel(entry.label)}
              </span>
              <span className="text-xs text-muted-foreground">
                {entry.value?.toLocaleString('en-GB')} ({pct}%)
              </span>
            </span>
          </>
        )

        if (!onItemClick) {
          return (
            <li
              key={`${entry.label}-${index}`}
              className={cn('flex items-start gap-2.5', isDimmed && 'opacity-50')}
            >
              {content}
            </li>
          )
        }

        return (
          <li key={`${entry.label}-${index}`}>
            <button
              type="button"
              className={cn(
                'flex w-full items-start gap-2.5 rounded-md px-1 py-1 text-left transition-colors hover:bg-muted/50',
                isActive && 'bg-primary/10 ring-1 ring-primary/30',
                isDimmed && 'opacity-50',
              )}
              onClick={() => onItemClick(entry.label)}
            >
              {content}
            </button>
          </li>
        )
      })}
    </ul>
  )
}

export function SimplePieChart({
  data,
  emptyMessage = 'No data',
  formatLabel,
  showLegend = true,
  activeLabel = null,
  onItemClick,
  colourFor,
  heightClassName,
  valueNoun = 'devices',
}) {
  const labelFormatter = formatLabel ?? ((l) => l)
  const clickable = typeof onItemClick === 'function'

  if (!data?.length) {
    return (
      <p className="flex h-[180px] items-center justify-center text-sm text-muted-foreground">
        {emptyMessage}
      </p>
    )
  }

  function handleSliceClick(_, index) {
    const entry = data[index]
    if (entry && onItemClick) {
      onItemClick(entry.label, entry)
    }
  }

  function sliceColour(entry, index) {
    return colourFor?.(entry, index) ?? CHART_COLOURS[index % CHART_COLOURS.length]
  }

  return (
    <div className={cn('flex flex-col gap-4', showLegend && 'sm:flex-row sm:items-stretch')}>
      <div
        className={cn(
          'mx-auto w-full shrink-0',
          heightClassName || 'h-[220px]',
          showLegend ? 'max-w-[200px] sm:mx-0' : 'max-w-[280px]',
        )}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={showLegend ? 48 : 58}
              outerRadius={showLegend ? 72 : 92}
              paddingAngle={2}
              onClick={clickable ? handleSliceClick : undefined}
              style={clickable ? { cursor: 'pointer' } : undefined}
            >
              {data.map((entry, index) => {
                const isActive = activeLabel === entry.label
                const isDimmed = activeLabel && !isActive
                return (
                  <Cell
                    key={`${entry.label}-${index}`}
                    fill={sliceColour(entry, index)}
                    stroke={isActive ? 'hsl(var(--primary))' : 'transparent'}
                    strokeWidth={isActive ? 3 : 0}
                    opacity={isDimmed ? 0.45 : 1}
                  />
                )
              })}
            </Pie>
            <Tooltip
              content={<ChartTooltip clickable={clickable} valueNoun={valueNoun} />}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      {showLegend ? (
        <PieLegend
          data={data}
          formatLabel={labelFormatter}
          activeLabel={activeLabel}
          onItemClick={
            clickable
              ? (label) => {
                  const entry = data.find((row) => row.label === label)
                  onItemClick(label, entry)
                }
              : undefined
          }
          colourFor={colourFor}
        />
      ) : null}
    </div>
  )
}
